import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type JSX, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OFFICE_CANVAS, OFFICE_DESKS, OFFICE_ZONES, MEETING_ROOMS, DESK_SIZE, deskSize, type BoardEntry, type DeskOrientation, type MeetingRoomStatus, type OfficeDesk } from '@dxtech/shared';
import { api } from '../api/client.js';
import { dateForOffset, useApp } from '../state/app.js';
import { ConfirmDialog, Modal, Spinner, Avatar } from '../ui/common.js';
import { STATUS, type WorkStatus } from '../ui/status.js';
import { boardStatus, kstHHmm } from '../lib/format.js';
import { DateNav } from '../ui/DateNav.js';
import { FoodCakeRegular } from '@fluentui/react-icons';

type Cell = WorkStatus | 'empty';
type SeatLayoutData = Awaited<ReturnType<typeof api.seatLayout>>;

function statusColor(s: Cell): { accent: string; fg: string; label: string } {
  if (s === 'empty') return { accent: '#E2E8F0', fg: '#C0C4CB', label: '공석' };
  const c = STATUS[s];
  return { accent: c.dot, fg: c.fg, label: c.label };
}

const LEGEND: WorkStatus[] = ['working', 'away', 'vacation', 'absent'];

// 좌석 이동/배치는 이 단위로만 스냅된다 — 미세 조정 대신 옆 좌석과 정렬을 쉽게 맞추기 위함.
const GRID_STEP = 4;
function snap(v: number): number {
  return Math.round(v / GRID_STEP) * GRID_STEP;
}
function clampPos(x: number, y: number, size: { w: number; h: number }): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(OFFICE_CANVAS.w - size.w, x)),
    y: Math.max(0, Math.min(OFFICE_CANVAS.h - size.h, y)),
  };
}

export function OfficeMap(): JSX.Element {
  const { dateOffset, me, flash } = useApp();
  const qc = useQueryClient();
  const canEdit = me.permissions.canManageConfig; // 좌석 배정·위치 이동 = Admin 전용
  const date = dateForOffset(dateOffset).date;
  const board = useQuery({ queryKey: ['board', date], queryFn: () => api.board(date) });
  const meetings = useQuery({ queryKey: ['meetings', date], queryFn: () => api.meetings(date) });
  const layout = useQuery({ queryKey: ['seat-layout'], queryFn: () => api.seatLayout() });
  const overrides = layout.data?.overrides ?? {};
  const deletedIds = useMemo(() => new Set(layout.data?.deletedIds ?? []), [layout.data]);
  // 기본(고정) 좌석 + 관리자가 추가한 커스텀 좌석 — 삭제된 좌석은 제외
  const desks = useMemo<OfficeDesk[]>(() => {
    const custom: OfficeDesk[] = (layout.data?.customDesks ?? []).map((d) => ({ id: d.id, label: d.label, zone: d.zone, x: d.x, y: d.y, orientation: d.orientation }));
    return [...OFFICE_DESKS, ...custom].filter((d) => !deletedIds.has(d.id));
  }, [layout.data, deletedIds]);
  const deskPos = (id: string, baseX: number, baseY: number): { x: number; y: number } => {
    const o = overrides[id];
    return o ? { x: o.x, y: o.y } : { x: baseX, y: baseY };
  };
  const deskOrientation = (id: string, base: DeskOrientation | undefined): DeskOrientation => overrides[id]?.orientation ?? base ?? 'H';
  const entries = board.data?.entries ?? [];

  const bySeat = useMemo(() => {
    const m = new Map<string, BoardEntry>();
    for (const e of entries) if (e.seatId) m.set(e.seatId, e);
    return m;
  }, [entries]);
  const roomStatus = useMemo(() => {
    const m = new Map<string, MeetingRoomStatus>();
    for (const r of meetings.data?.rooms ?? []) m.set(r.roomId, r);
    return m;
  }, [meetings.data]);

  const counts = useMemo(() => {
    const c: Record<WorkStatus, number> = { working: 0, away: 0, vacation: 0, off: 0, absent: 0, trip: 0, exempt: 0 };
    for (const e of entries) c[boardStatus(e)] += 1;
    return c;
  }, [entries]);

  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = (): void => setScale(Math.min(1, el.clientWidth / OFFICE_CANVAS.w));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [detail, setDetail] = useState<string | null>(null);
  const [assignSeatId, setAssignSeatId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ seatId: string; x: number; y: number } | null>(null);
  const [room, setRoom] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  // 위치 이동 모드: { seatId, x, y, orientation } = 편집 중인 좌석의 실시간 상태(캔버스 좌표)
  const canvasRef = useRef<HTMLDivElement>(null);
  const [moving, setMoving] = useState<{ seatId: string; x: number; y: number; orientation: DeskOrientation } | null>(null);

  const moveSeat = useMutation({
    mutationFn: ({ seatId, x, y, orientation }: { seatId: string; x: number; y: number; orientation: DeskOrientation }) => api.moveSeat(seatId, x, y, orientation),
    onMutate: ({ seatId, x, y, orientation }) => {
      // 낙관적 갱신: 새로고침 전에도 새 위치 즉시 유지
      qc.setQueryData<SeatLayoutData>(['seat-layout'], (old) => ({
        overrides: { ...(old?.overrides ?? {}), [seatId]: { x, y, orientation } },
        customDesks: old?.customDesks ?? [],
        deletedIds: old?.deletedIds ?? [],
      }));
    },
    onSuccess: () => { flash('좌석 위치를 저장했습니다'); qc.invalidateQueries({ queryKey: ['seat-layout'] }); },
    onError: (e) => { flash(e instanceof Error ? e.message : '위치 저장 실패'); qc.invalidateQueries({ queryKey: ['seat-layout'] }); },
  });

  const createSeat = useMutation({
    mutationFn: ({ label, zone, x, y, orientation }: { label: string; zone: '업무공간' | '연구소'; x: number; y: number; orientation: DeskOrientation }) =>
      api.createSeat(label, zone, x, y, orientation),
    onSuccess: (res, vars) => {
      flash('좌석을 추가했습니다 — 위치를 조정해 주세요');
      // 낙관적 갱신: 재조회 전에도 새 좌석이 즉시 보이고 바로 이동 모드로 들어갈 수 있도록
      qc.setQueryData<SeatLayoutData>(['seat-layout'], (old) => ({
        overrides: old?.overrides ?? {},
        customDesks: [...(old?.customDesks ?? []), { id: res.id, label: vars.label, zone: vars.zone, x: vars.x, y: vars.y, orientation: vars.orientation }],
        deletedIds: old?.deletedIds ?? [],
      }));
      qc.invalidateQueries({ queryKey: ['seat-layout'] });
      setAddOpen(false);
      setMoving({ seatId: res.id, x: vars.x, y: vars.y, orientation: vars.orientation });
    },
    onError: (e) => flash(e instanceof Error ? e.message : '좌석 추가 실패'),
  });

  const deleteSeat = useMutation({
    mutationFn: (seatId: string) => api.deleteSeat(seatId),
    onSuccess: () => {
      flash('좌석을 삭제했습니다');
      qc.invalidateQueries({ queryKey: ['seat-layout'] });
      qc.invalidateQueries({ queryKey: ['board'] });
      setDeleteTarget(null);
    },
    onError: (e) => { flash(e instanceof Error ? e.message : '삭제 실패'); setDeleteTarget(null); },
  });

  // 이동 모드: 마우스 이동 → 위치 갱신(그리드 스냅), 클릭/적용 → 저장, ESC/취소 → 취소
  const onCanvasMove = (ev: ReactMouseEvent): void => {
    if (!moving || !canvasRef.current) return;
    const size = deskSize(moving.orientation);
    const rect = canvasRef.current.getBoundingClientRect();
    const rawX = (ev.clientX - rect.left) / scale - size.w / 2;
    const rawY = (ev.clientY - rect.top) / scale - size.h / 2;
    const { x, y } = clampPos(snap(rawX), snap(rawY), size);
    setMoving({ ...moving, x, y });
  };
  const onCanvasDrop = (): void => {
    if (!moving) return;
    moveSeat.mutate(moving);
    setMoving(null);
  };
  // 키보드 화살표: GRID_STEP 단위로 이동. X/Y 입력 중엔 글자 입력으로 처리(화면 전체 핸들러 무시).
  useEffect(() => {
    if (!moving) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') { setMoving(null); return; }
      const tag = (document.activeElement?.tagName ?? '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const size = deskSize(moving.orientation);
      let dx = 0, dy = 0;
      if (e.key === 'ArrowLeft') dx = -GRID_STEP;
      else if (e.key === 'ArrowRight') dx = GRID_STEP;
      else if (e.key === 'ArrowUp') dy = -GRID_STEP;
      else if (e.key === 'ArrowDown') dy = GRID_STEP;
      else return;
      e.preventDefault();
      const { x, y } = clampPos(moving.x + dx, moving.y + dy, size);
      setMoving({ ...moving, x, y });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moving]);

  return (
    <div className="dx-card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '16px 18px 13px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>근무 현황</div>
        <div style={{ display: 'flex', gap: 10, fontSize: 12, color: '#6B7280', flexWrap: 'wrap' }}>
          {LEGEND.map((s) => (
            <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS[s].dot }} />
              {STATUS[s].label} <b style={{ color: '#1F2937' }}>{counts[s]}</b>
            </span>
          ))}
        </div>
        {canEdit && (
          <button className="dx-btn dx-btn-ghost" style={{ padding: '5px 10px', fontSize: 12.5 }} onClick={() => setAddOpen(true)}>+ 좌석 추가</button>
        )}
        <div style={{ marginLeft: 'auto' }}><DateNav /></div>
      </div>

      {board.isLoading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
      ) : (
        <div style={{ padding: 14 }}>
          <div ref={wrapRef} style={{ overflow: 'hidden', height: OFFICE_CANVAS.h * scale }}>
            <div ref={canvasRef}
              onMouseMove={moving ? onCanvasMove : undefined}
              onClick={moving ? onCanvasDrop : undefined}
              style={{ width: OFFICE_CANVAS.w, height: OFFICE_CANVAS.h, position: 'relative', transform: `scale(${scale})`, transformOrigin: 'top left', background: '#FCFCFD', border: '2.5px solid #111827', borderRadius: 14, cursor: moving ? 'move' : 'default' }}>
              {/* 구역(연구소) */}
              {OFFICE_ZONES.map((z, i) => (
                <div key={i} style={{ position: 'absolute', left: z.x, top: z.y, width: z.w, height: z.h, border: '1px solid #ECE7DC', borderRadius: 8, background: '#FBFAF7', boxSizing: 'border-box', padding: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#9CA3AF' }}>{z.label}</span>
                </div>
              ))}

              {/* 회의실 */}
              {MEETING_ROOMS.map((r) => {
                const st = roomStatus.get(r.id);
                const inUse = !!st?.inUse;
                return (
                  <button key={r.id} onClick={() => setRoom(r.id)} style={{
                    position: 'absolute', left: r.x, top: r.y, width: r.w, height: r.h,
                    background: '#fff', border: `1.5px solid ${inUse ? '#FBCFCB' : '#D7E8DC'}`, borderRadius: 12,
                    padding: '14px 15px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box',
                    boxShadow: '0 1px 2px rgba(16,24,40,.05)', pointerEvents: moving ? 'none' : undefined,
                    display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'stretch',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>{r.label}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: inUse ? '#FEE2E2' : '#DCFCE7', color: inUse ? '#B91C1C' : '#15803D' }}>{inUse ? '사용중' : '사용 가능'}</span>
                    </div>
                    {inUse && st?.current ? (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#B91C1C' }}>{kstHHmm(st.current.startTime)} – {kstHHmm(st.current.endTime)}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{st.current.topic || '회의'}</div>
                        <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>예약자 {st.current.hostName ?? '—'}</div>
                      </div>
                    ) : (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#15803D' }}>지금 사용 가능</div>
                        {st?.next && (
                          <div style={{ marginTop: 4 }}>
                            <div style={{ fontSize: 11.5, color: '#6B7280' }}>다음 {kstHHmm(st.next.startTime)} – {kstHHmm(st.next.endTime)}</div>
                            <div style={{ fontSize: 11.5, color: '#6B7280', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{st.next.topic || '회의'} · {st.next.hostName ?? ''}</div>
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#C0C4CB', position: 'absolute', bottom: 10, right: 14 }}>클릭하여 예약</div>
                  </button>
                );
              })}

              {/* 좌석 */}
              {desks.map((d) => {
                const e = bySeat.get(d.id);
                const s: Cell = e ? boardStatus(e) : 'empty';
                const c = statusColor(s);
                const mine = e?.upn === me.upn;
                const isMoving = moving?.seatId === d.id;
                const orientation = isMoving ? moving!.orientation : deskOrientation(d.id, d.orientation);
                const size = deskSize(orientation);
                const pos = isMoving ? { x: moving!.x, y: moving!.y } : deskPos(d.id, d.x, d.y);
                // 그날 생일(올해 양력 기준, 음력은 서버에서 변환) — 좌석 위에 케이크
                const birthday = !!e?.birthdaySolar && e.birthdaySolar === date.slice(5);
                return (
                  <Fragment key={d.id}>
                  {birthday && !isMoving && (
                    <span title={`${e!.name} 님 생일`} aria-label={`${e!.name} 님 생일`}
                      style={{ position: 'absolute', left: pos.x + size.w / 2 - 13, top: pos.y - 24, width: 26, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#FDF2F8', border: '1px solid #FBCFE8', borderRadius: 8, color: '#DB2777', zIndex: 5, pointerEvents: 'none' }}>
                      <FoodCakeRegular style={{ fontSize: 16 }} />
                    </span>
                  )}
                  <button title={d.label}
                    onClick={() => { if (!moving) setDetail(d.id); }}
                    onContextMenu={(ev) => { ev.preventDefault(); if (!moving) setMenu({ seatId: d.id, x: ev.clientX, y: ev.clientY }); }}
                    style={{
                    position: 'absolute', left: pos.x, top: pos.y, width: size.w, height: size.h,
                    background: e ? '#fff' : '#FCFCFD', borderRadius: 9, cursor: 'pointer', textAlign: 'left',
                    border: e ? '1px solid #E8EAEE' : '1.5px dashed #D1D5DB',
                    boxShadow: isMoving ? '0 8px 24px rgba(37,99,235,.35), 0 0 0 2px #2563EB' : mine ? '0 0 0 1.5px #93B4F7' : '0 1px 2px rgba(16,24,40,.05)',
                    padding: '9px 11px', overflow: 'hidden', fontFamily: 'inherit',
                    borderLeft: e ? `3px solid ${c.accent}` : undefined,
                    // 이동 중: 끌리는 좌석은 위로 띄우고 클릭이 캔버스(드롭)로 가도록 pointerEvents 해제,
                    // 나머지 좌석도 pointerEvents 해제해 캔버스가 마우스 이벤트를 받게 함
                    pointerEvents: moving ? 'none' : undefined,
                    zIndex: isMoving ? 60 : undefined,
                    opacity: isMoving ? 0.92 : undefined,
                  }}>
                    {e ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Avatar name={e.name} upn={e.upn} size={22} index={0} />
                          <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.name}</span>
                        </div>
                        <div style={{ fontSize: 10.5, fontWeight: 600, color: '#9CA3AF', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.accent, flexShrink: 0 }} />{c.label}{mine ? ' · 내 자리' : ''}
                        </div>
                      </>
                    ) : (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#C0C4CB' }}>공석</span>
                      </div>
                    )}
                  </button>
                  </Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {menu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 70 }} onClick={() => setMenu(null)} onContextMenu={(ev) => { ev.preventDefault(); setMenu(null); }} />
          <div style={{ position: 'fixed', left: Math.min(menu.x, window.innerWidth - 180), top: menu.y, zIndex: 71, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, boxShadow: '0 10px 30px rgba(16,24,40,.18)', padding: 6, minWidth: 164 }}>
            <MenuItem onClick={() => { setDetail(menu.seatId); setMenu(null); }}>상세 보기</MenuItem>
            {bySeat.get(menu.seatId) && (
              <MenuItem onClick={() => { const u = bySeat.get(menu.seatId)!.upn; window.open(`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(u)}`, '_blank'); setMenu(null); }}>메시지 보내기 (Teams)</MenuItem>
            )}
            {canEdit && (
              <MenuItem onClick={() => { setAssignSeatId(menu.seatId); setMenu(null); }}>{bySeat.get(menu.seatId) ? '좌석 재배정' : '좌석 배정'}</MenuItem>
            )}
            {canEdit && (
              <MenuItem onClick={() => {
                const d = desks.find((x) => x.id === menu.seatId);
                const p = deskPos(menu.seatId, d?.x ?? 0, d?.y ?? 0);
                setMoving({ seatId: menu.seatId, x: p.x, y: p.y, orientation: deskOrientation(menu.seatId, d?.orientation) });
                setMenu(null);
              }}>위치 이동 · 방향 전환</MenuItem>
            )}
            {canEdit && (
              <MenuItem onClick={() => { setDeleteTarget(menu.seatId); setMenu(null); }}>좌석 삭제</MenuItem>
            )}
          </div>
        </>
      )}

      {/* 위치 이동 모드 안내 배너 — 마우스 드래그, 화살표 키(그리드 단위), X/Y 직접 입력, 방향 전환 모두 지원 */}
      {moving && (
        <div style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 80, background: '#111827', color: '#fff', borderRadius: 14, padding: '12px 18px', fontSize: 13, fontWeight: 600, boxShadow: '0 10px 30px rgba(16,24,40,.3)', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ color: '#D1D5DB', fontWeight: 500 }}>마우스/화살표로 이동({GRID_STEP}px 단위) · 아래 좌표 직접 입력</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            X
            <input type="number" step={GRID_STEP} value={moving.x}
              onChange={(ev) => {
                const size = deskSize(moving.orientation);
                const { x } = clampPos(snap(Number(ev.target.value) || 0), moving.y, size);
                setMoving({ ...moving, x });
              }}
              style={{ width: 64, background: '#1F2937', border: '1px solid #374151', color: '#fff', borderRadius: 6, padding: '4px 6px', fontSize: 12.5 }} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            Y
            <input type="number" step={GRID_STEP} value={moving.y}
              onChange={(ev) => {
                const size = deskSize(moving.orientation);
                const { y } = clampPos(moving.x, snap(Number(ev.target.value) || 0), size);
                setMoving({ ...moving, y });
              }}
              style={{ width: 64, background: '#1F2937', border: '1px solid #374151', color: '#fff', borderRadius: 6, padding: '4px 6px', fontSize: 12.5 }} />
          </label>
          <button onClick={() => {
            const next: DeskOrientation = moving.orientation === 'V' ? 'H' : 'V';
            const { x, y } = clampPos(moving.x, moving.y, deskSize(next));
            setMoving({ ...moving, orientation: next, x, y });
          }} style={{ border: '1px solid #374151', background: 'transparent', color: '#D1D5DB', borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {moving.orientation === 'V' ? '세로형' : '가로형'} ⇄
          </button>
          <button onClick={onCanvasDrop} className="dx-btn dx-btn-primary" style={{ padding: '5px 12px', fontSize: 12.5 }}>적용</button>
          <button onClick={() => setMoving(null)} style={{ border: '1px solid #374151', background: 'transparent', color: '#D1D5DB', borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>취소</button>
        </div>
      )}
      {detail && <SeatDetailModal entry={bySeat.get(detail) ?? null} onClose={() => setDetail(null)} />}
      {assignSeatId && <SeatModal seatId={assignSeatId} entry={bySeat.get(assignSeatId) ?? null} canEdit={canEdit} onClose={() => setAssignSeatId(null)} />}
      {room && <MeetingReserveModal roomId={room} date={date} onClose={() => setRoom(null)} />}
      {addOpen && (
        <AddSeatModal
          busy={createSeat.isPending}
          onClose={() => setAddOpen(false)}
          onSubmit={(label, zone) => {
            const { x, y } = clampPos(OFFICE_CANVAS.w / 2 - DESK_SIZE.w / 2, OFFICE_CANVAS.h / 2 - DESK_SIZE.h / 2, DESK_SIZE);
            createSeat.mutate({ label, zone, x, y, orientation: 'H' });
          }}
        />
      )}
      {deleteTarget && (
        <ConfirmDialog
          title="좌석 삭제"
          message={`'${desks.find((d) => d.id === deleteTarget)?.label ?? deleteTarget}' 좌석을 삭제할까요? 배정된 임직원이 있으면 먼저 비웁니다.`}
          confirmLabel="삭제" danger busy={deleteSeat.isPending}
          onConfirm={() => deleteSeat.mutate(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function AddSeatModal({ onClose, onSubmit, busy }: { onClose: () => void; onSubmit: (label: string, zone: '업무공간' | '연구소') => void; busy: boolean }): JSX.Element {
  const [label, setLabel] = useState('');
  const [zone, setZone] = useState<'업무공간' | '연구소'>('업무공간');
  return (
    <Modal width={360} onClose={onClose}>
      <div style={{ padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>좌석 추가</div>
          <button title="닫기" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', fontSize: 26, lineHeight: 1 }}>×</button>
        </div>
        <label style={{ ...field, marginBottom: 12 }}><span style={lbl}>좌석 이름</span>
          <input className="dx-input" placeholder="예: #12" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
        </label>
        <label style={{ ...field, marginBottom: 16 }}><span style={lbl}>구역</span>
          <select className="dx-select" value={zone} onChange={(e) => setZone(e.target.value as '업무공간' | '연구소')}>
            <option value="업무공간">업무공간</option>
            <option value="연구소">연구소</option>
          </select>
        </label>
        <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 16 }}>추가 후 캔버스 중앙에 배치되며, 바로 위치를 옮길 수 있습니다.</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="dx-btn dx-btn-ghost" style={{ flex: 1 }} onClick={onClose}>취소</button>
          <button className="dx-btn dx-btn-primary" style={{ flex: 1 }} disabled={!label.trim() || busy} onClick={() => onSubmit(label.trim(), zone)}>추가</button>
        </div>
      </div>
    </Modal>
  );
}

function SeatModal({ seatId, entry, canEdit, onClose }: {
  seatId: string; entry: BoardEntry | null; canEdit: boolean; onClose: () => void;
}): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const [pick, setPick] = useState('');
  const emps = useQuery({ queryKey: ['employees'], queryFn: () => api.employees(), enabled: canEdit });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['board'] }); qc.invalidateQueries({ queryKey: ['employees'] }); };
  const assign = useMutation({
    mutationFn: (upn: string) => api.assignSeat(seatId, upn),
    onSuccess: () => { flash('좌석을 배정했습니다'); refresh(); onClose(); },
    onError: (e) => flash(e instanceof Error ? e.message : '배정 실패'),
  });
  const unassign = useMutation({
    mutationFn: () => api.unassignSeat(seatId),
    onSuccess: () => { flash('좌석을 비웠습니다'); refresh(); onClose(); },
    onError: (e) => flash(e instanceof Error ? e.message : '해제 실패'),
  });
  const s: Cell = entry ? boardStatus(entry) : 'empty';
  const c = statusColor(s);

  return (
    <Modal width={400} onClose={onClose}>
      <div style={{ padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>좌석 정보</div>
          <button title="닫기" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', fontSize: 26, lineHeight: 1 }}>×</button>
        </div>
        {entry ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#F9FAFB', border: '1px solid #EDEFF2', borderRadius: 9, marginBottom: 14 }}>
            <Avatar name={entry.name} index={0} size={34} upn={entry.upn} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{entry.name} <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 400 }}>{entry.dept}</span></div>
              <div style={{ fontSize: 12, color: c.fg, fontWeight: 600 }}>{c.label}{entry.schedule?.[0]?.subject ? ` · ${entry.schedule[0].subject}` : ''}</div>
            </div>
          </div>
        ) : (
          <div style={{ padding: '14px 12px', background: '#FCFCFD', border: '1.5px dashed #D1D5DB', borderRadius: 9, marginBottom: 14, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>공석</div>
        )}
        {!canEdit ? (
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>좌석 배정은 관리자만 변경할 수 있습니다.</div>
        ) : (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>임직원 배정</div>
            {emps.isLoading ? (
              <div style={{ padding: 16, textAlign: 'center' }}><Spinner size={22} /></div>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <select className="dx-select" style={{ flex: 1 }} value={pick} onChange={(e) => setPick(e.target.value)}>
                  <option value="">— 선택 —</option>
                  {(emps.data?.items ?? []).filter((x) => x.active).map((x) => (
                    <option key={x.upn} value={x.upn}>{x.name} ({x.dept})</option>
                  ))}
                </select>
                <button className="dx-btn dx-btn-primary" disabled={!pick || assign.isPending} onClick={() => assign.mutate(pick)}>배정</button>
              </div>
            )}
            {entry && (
              <button className="dx-btn dx-btn-ghost" style={{ width: '100%', marginTop: 10 }} disabled={unassign.isPending} onClick={() => unassign.mutate()}>이 좌석 비우기</button>
            )}
          </>
        )}
        <div style={{ display: 'flex', marginTop: 16 }}>
          <button className="dx-btn dx-btn-ghost" style={{ flex: 1 }} onClick={onClose}>닫기</button>
        </div>
      </div>
    </Modal>
  );
}

function MeetingReserveModal({ roomId, date, onClose }: { roomId: string; date: string; onClose: () => void }): JSX.Element {
  const { flash, me } = useApp();
  const qc = useQueryClient();
  const room = MEETING_ROOMS.find((r) => r.id === roomId);
  const today = dateForOffset(0).date;
  const [resvDate, setResvDate] = useState(date < today ? today : date);
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('10:00');
  const [topic, setTopic] = useState('');
  const [pending, setPending] = useState<{ type: 'end' | 'cancel'; id: string } | null>(null);
  const upcoming = useQuery({ queryKey: ['meetings-upcoming'], queryFn: () => api.meetingsUpcoming() });
  const list = (upcoming.data?.items ?? []).filter((m) => m.roomId === roomId);
  const invalid = end <= start;
  const isAdmin = me.permissions.canViewAdmin;
  const invalidateMeetings = () => { qc.invalidateQueries({ queryKey: ['meetings'] }); qc.invalidateQueries({ queryKey: ['meetings-upcoming'] }); };

  const create = useMutation({
    mutationFn: () => api.createMeeting({ roomId, date: resvDate, startTime: start, endTime: end, topic: topic.trim() || undefined }),
    onSuccess: () => { flash('회의실을 예약했습니다'); invalidateMeetings(); onClose(); },
    onError: (e) => flash(e instanceof Error ? e.message : '예약 실패'),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelMeeting(id),
    onSuccess: () => { flash('예약을 삭제했습니다'); invalidateMeetings(); },
    onError: (e) => flash(e instanceof Error ? e.message : '삭제 실패'),
  });
  const endNow = useMutation({
    mutationFn: (id: string) => api.endMeeting(id),
    onSuccess: () => { flash('회의를 종료했습니다 (사용완료)'); invalidateMeetings(); },
    onError: (e) => flash(e instanceof Error ? e.message : '종료 실패'),
  });

  return (
    <Modal width={440} onClose={onClose}>
      <div style={{ padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{room?.label ?? '회의실'} 예약</div>
          <button title="닫기" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', fontSize: 26, lineHeight: 1 }}>×</button>
        </div>

        <label style={{ ...field, marginBottom: 12 }}><span style={lbl}>날짜</span>
          <input type="date" className="dx-input" value={resvDate} min={today} onChange={(e) => setResvDate(e.target.value || today)} />
        </label>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <label style={field}><span style={lbl}>시작</span><input type="time" className="dx-input" value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label style={field}><span style={lbl}>종료</span><input type="time" className="dx-input" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        </div>
        {invalid && <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#B91C1C', borderRadius: 8, padding: '8px 11px', fontSize: 12.5, fontWeight: 600, marginBottom: 12 }}>종료 시간이 시작 시간보다 늦어야 합니다.</div>}
        <label style={{ ...field, marginBottom: 14 }}><span style={lbl}>안건</span>
          <input className="dx-input" placeholder="회의 안건(선택)" value={topic} onChange={(e) => setTopic(e.target.value)} />
        </label>

        <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>예약 현황 (현재·이후 전체)</div>
        <div style={{ maxHeight: 170, overflow: 'auto', border: '1px solid #EDEFF2', borderRadius: 8 }}>
          {upcoming.isLoading ? (
            <div style={{ padding: 16, textAlign: 'center' }}><Spinner size={20} /></div>
          ) : list.length === 0 ? (
            <div style={{ padding: 14, textAlign: 'center', color: '#9CA3AF', fontSize: 12.5 }}>예정된 예약 없음</div>
          ) : list.map((m) => {
            const mine = isAdmin || m.hostUpn?.toLowerCase() === me.upn.toLowerCase();
            const nowMs = Date.now();
            const inProgress = Date.parse(m.startTime) <= nowMs && nowMs < Date.parse(m.endTime);
            return (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #F6F7F9', fontSize: 12.5 }}>
                <span style={{ color: '#9CA3AF', whiteSpace: 'nowrap', flexShrink: 0 }}>{dateForOffset0Label(m.date)}</span>
                <span className="tnum" style={{ fontWeight: 600, color: inProgress ? '#B91C1C' : '#1F2937', whiteSpace: 'nowrap' }}>{kstHHmm(m.startTime)}–{kstHHmm(m.endTime)}</span>
                <span style={{ flex: 1, color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.topic || '회의'}</span>
                <span style={{ color: '#9CA3AF', whiteSpace: 'nowrap' }}>{m.hostName ?? ''}</span>
                {mine && inProgress && (
                  <button title="사용완료(회의 종료)" onClick={() => setPending({ type: 'end', id: m.id })}
                    style={{ border: '1px solid #FCA5A5', background: '#FEF2F2', cursor: 'pointer', color: '#B91C1C', fontSize: 11, fontWeight: 700, lineHeight: 1, padding: '3px 7px', borderRadius: 6, flexShrink: 0, whiteSpace: 'nowrap' }}>사용완료</button>
                )}
                {mine && !inProgress && (
                  <button title="예약 삭제" onClick={() => setPending({ type: 'cancel', id: m.id })}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#DC2626', fontSize: 15, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}>×</button>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button className="dx-btn dx-btn-ghost" style={{ flex: 1 }} onClick={onClose}>취소</button>
          <button className="dx-btn dx-btn-primary" style={{ flex: 1 }} disabled={invalid || create.isPending} onClick={() => create.mutate()}>예약</button>
        </div>
      </div>
      {pending && (
        <ConfirmDialog
          title={pending.type === 'end' ? '회의 종료' : '예약 삭제'}
          message={pending.type === 'end' ? '회의를 사용완료(종료) 처리할까요?' : '이 예약을 삭제할까요?'}
          confirmLabel={pending.type === 'end' ? '사용완료' : '삭제'} danger={pending.type === 'cancel'}
          busy={endNow.isPending || cancel.isPending}
          onConfirm={() => { if (pending.type === 'end') endNow.mutate(pending.id); else cancel.mutate(pending.id); setPending(null); }}
          onCancel={() => setPending(null)} />
      )}
    </Modal>
  );
}

function MenuItem({ onClick, children }: { onClick: () => void; children: ReactNode }): JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 13, color: '#1F2937', padding: '8px 12px', borderRadius: 7, cursor: 'pointer' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F4F6'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >{children}</button>
  );
}

function Kv({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
      <span style={{ color: '#6B7280', flexShrink: 0 }}>{k}</span>
      <span title={v} style={{ fontWeight: 600, color: '#1F2937', minWidth: 0, textAlign: 'right', wordBreak: 'break-word' }}>{v}</span>
    </div>
  );
}

/** 'YYYY-MM-DD' → 'M/D' */
function mdDot(d?: string): string {
  if (!d || d.length < 10) return d ?? '';
  return `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
}

function SeatDetailModal({ entry, onClose }: { entry: BoardEntry | null; onClose: () => void }): JSX.Element {
  const s: Cell = entry ? boardStatus(entry) : 'empty';
  const c = statusColor(s);
  const sendTeams = (): void => {
    if (entry) window.open(`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(entry.upn)}`, '_blank');
  };
  return (
    <Modal width={380} onClose={onClose}>
      <div style={{ padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>상세 보기</div>
          <button title="닫기" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', fontSize: 26, lineHeight: 1 }}>×</button>
        </div>
        {entry ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <Avatar name={entry.name} upn={entry.upn} size={44} index={0} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{entry.name}</div>
                <div style={{ fontSize: 12.5, color: '#9CA3AF' }}>{entry.dept}</div>
                <div style={{ fontSize: 12.5, color: c.fg, fontWeight: 600, marginTop: 2 }}>● {c.label}</div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* 상태별 상세: 휴가 / 자리비움 / 출장 */}
              {s === 'vacation' && entry.leave ? (
                <>
                  <Kv k="휴가" v={entry.leave.type} />
                  <Kv k="기간" v={`${mdDot(entry.leave.startDate)}${entry.leave.endDate !== entry.leave.startDate ? ` ~ ${mdDot(entry.leave.endDate)}` : ''}${entry.leave.days != null ? ` · ${entry.leave.days}일` : ''}`} />
                  {entry.leave.reason && <Kv k="사유" v={entry.leave.reason} />}
                </>
              ) : entry.away ? (
                <>
                  <Kv k="자리비움" v={`${kstHHmm(entry.away.startTime)} ~ ${kstHHmm(entry.away.endTime)}`} />
                  {entry.away.reason && <Kv k="사유" v={entry.away.reason} />}
                </>
              ) : s === 'trip' && entry.trip ? (
                <>
                  <Kv k="출장지" v={entry.trip.location || '—'} />
                  <Kv k="기간" v={`${mdDot(entry.trip.startDate)}${entry.trip.endDate !== entry.trip.startDate ? ` ~ ${mdDot(entry.trip.endDate)}` : ''}`} />
                  {entry.trip.detail && <Kv k="내역" v={entry.trip.detail} />}
                </>
              ) : (
                <Kv k="일정" v={entry.schedule?.[0]?.subject ?? '일정 없음'} />
              )}
              {entry.phone && <Kv k="연락처" v={entry.phone} />}
              {entry.birthdaySolar && <Kv k="생일" v={`${Number(entry.birthdaySolar.slice(0, 2))}/${Number(entry.birthdaySolar.slice(3, 5))}`} />}
            </div>
            <button className="dx-btn dx-btn-primary" style={{ width: '100%', marginTop: 16 }} onClick={sendTeams}>메시지 보내기 (Teams)</button>
          </>
        ) : (
          <div style={{ padding: 18, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>공석입니다.</div>
        )}
      </div>
    </Modal>
  );
}

function dateForOffset0Label(date: string): string {
  const p = date.split('-');
  return p.length === 3 ? `${Number(p[1])}/${Number(p[2])}` : date;
}

const field = { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 } as const;
const lbl = { fontSize: 12, fontWeight: 600, color: '#6B7280' } as const;
